/**
 * Dependency Injection Container
 *
 * Each SPA bundle gets its own instance of DiContainer (singleton per bundle).
 * The container stores registered state and services that can be retrieved
 * by components and other services via the @inject decorator or resolve() function.
 */

class DiContainerClass {
  private registry = new Map<string, unknown>();
  private initialized = false;

  /**
   * Initialize the container with all dependencies at once.
   * This is the primary way to set up the container.
   *
   * @example
   * DiContainer.initialize({
   *   counterState: new CounterState(),
   *   counterService: new CounterService(),
   * });
   */
  initialize(dependencies: Record<string, unknown>): void {
    if (this.initialized) {
      throw new Error("DiContainer: Container is already initialized.");
    }

    for (const [key, value] of Object.entries(dependencies)) {
      if (this.registry.has(key)) {
        throw new Error(`DiContainer: Key "${key}" is already registered.`);
      }
      this.registry.set(key, value);
    }

    this.initialized = true;
  }

  /**
   * Register a state or service with a unique key.
   * Can only be called after initialize() for dynamic additions.
   * @throws Error if container not initialized or key already registered
   */
  register<T>(key: string, value: T): void {
    if (!this.initialized) {
      throw new Error(
        `DiContainer: Cannot register "${key}" before initialization.\n` + `Call DiContainer.initialize({...}) first.`,
      );
    }
    if (this.registry.has(key)) {
      throw new Error(`DiContainer: Key "${key}" is already registered.`);
    }
    this.registry.set(key, value);
  }

  /**
   * Get a registered state or service by key.
   * @throws Error if the container is not initialized or key is not registered
   */
  get<T>(key: string): T {
    if (!this.initialized) {
      throw new Error(
        `DiContainer: Attempted to resolve "${key}" before container was initialized.\n` +
          `Call DiContainer.initialize({...}) in your app's entry point before any components mount.\n` +
          `Registered keys: ${this.keys().join(", ") || "(none)"}`,
      );
    }

    const value = this.registry.get(key);
    if (value === undefined) {
      const availableKeys = this.keys();
      const suggestion = this.findSimilarKey(key, availableKeys);

      throw new Error(
        `DiContainer: No provider registered for "${key}".\n` +
          `Available keys: ${availableKeys.join(", ") || "(none)"}\n` +
          (suggestion ? `Did you mean "${suggestion}"?\n` : "") +
          `Ensure the service is included in DiContainer.initialize({...}).`,
      );
    }
    return value as T;
  }

  /**
   * Check if a key is registered.
   */
  has(key: string): boolean {
    return this.registry.has(key);
  }

  /**
   * Replace an existing registration or register new.
   * Useful for testing when you need to swap implementations.
   */
  override<T>(key: string, value: T): void {
    this.registry.set(key, value);
  }

  /**
   * Check if the container has been initialized.
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Clear all registrations. Useful for testing.
   */
  clear(): void {
    this.registry.clear();
    this.initialized = false;
  }

  /**
   * Get all registered keys. Useful for debugging.
   */
  keys(): string[] {
    return Array.from(this.registry.keys());
  }

  /**
   * Find a similar key for typo detection.
   * Uses substring matching and Levenshtein distance heuristics.
   */
  private findSimilarKey(key: string, availableKeys: string[]): string | null {
    const lower = key.toLowerCase();

    // Check for substring matches first
    const substringMatch = availableKeys.find(
      (k) => k.toLowerCase().includes(lower) || lower.includes(k.toLowerCase()),
    );
    if (substringMatch) return substringMatch;

    // Check for close Levenshtein distance (typos)
    for (const candidate of availableKeys) {
      if (this.levenshteinDistance(candidate.toLowerCase(), lower) <= 2) {
        return candidate;
      }
    }
    return null;
  }

  /**
   * Calculate Levenshtein distance between two strings.
   */
  private levenshteinDistance(a: string, b: string): number {
    // Initialize matrix with proper dimensions
    const matrix: number[][] = Array.from({ length: b.length + 1 }, () =>
      Array.from({ length: a.length + 1 }, () => 0),
    );

    // Initialize first column
    for (let i = 0; i <= b.length; i++) {
      matrix[i]![0] = i;
    }
    // Initialize first row
    for (let j = 0; j <= a.length; j++) {
      matrix[0]![j] = j;
    }

    // Fill in the rest of the matrix
    for (let i = 1; i <= b.length; i++) {
      for (let j = 1; j <= a.length; j++) {
        if (b.charAt(i - 1) === a.charAt(j - 1)) {
          matrix[i]![j] = matrix[i - 1]![j - 1]!;
        } else {
          matrix[i]![j] = Math.min(
            matrix[i - 1]![j - 1]! + 1, // substitution
            matrix[i]![j - 1]! + 1, // insertion
            matrix[i - 1]![j]! + 1, // deletion
          );
        }
      }
    }
    return matrix[b.length]![a.length]!;
  }
}

/**
 * Singleton instance of DiContainer.
 * Each SPA bundle gets its own instance due to module isolation.
 */
export const DiContainer = new DiContainerClass();
